import type {Mapper} from '@automapper/core';
import {constructUsing, createMap, forMember, mapFrom} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {PayoutReport} from '@igp/shared/contract/pam';
import type {IsNever} from '@igp/shared/utils';

import type {
    PayoutReportColumns,
    PayoutReportDownloadFields,
    PayoutReportDownloadModel,
    PayoutReportQueryFields,
    PayoutReportSortKeys,
    PayoutReportViewModel,
    PayoutReportViewModelKeys,
} from './types';

export const payoutReportMappingKeys = {
    model: 'PayoutReportModel',
    viewModel: 'PayoutReportViewModel',
    downloadModel: 'PayoutReportDownloadModel',
    viewModelsKeys: 'PayoutReportViewModelKeys',
    queryItemsKeys: 'PayoutReportQueryItemsKeys',
    downloadFields: 'PayoutReportDownloadFields',
    columns: 'PayoutReportColumns',
    sortKeys: 'PayoutReportSortKeys',
} as const;

export class PayoutReportMapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<PayoutReport, PayoutReportViewModel>(
            mapper,
            payoutReportMappingKeys.model,
            payoutReportMappingKeys.viewModel,
            forMember(
                vm => vm.agent_uid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.agent_username,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.casino,
                mapFrom(m => m.casino)
            ),
            forMember(
                vm => vm.country,
                mapFrom(m => m.country)
            ),
            forMember(
                vm => vm.global_spins,
                mapFrom(m => m.global_spins)
            ),
            forMember(
                vm => vm.payout_report_id,
                mapFrom(m => m.payout_report_id)
            ),
            forMember(
                vm => vm.player_uid,
                mapFrom(m => m.player_uid)
            ),
            forMember(
                vm => vm.player_username,
                mapFrom(m => m.player_username)
            ),
            forMember(
                vm => vm.poker_cash,
                mapFrom(m => m.poker_cash)
            ),
            forMember(
                vm => vm.poker_mtt,
                mapFrom(m => m.poker_mtt)
            ),
            forMember(
                vm => vm.poker_plo,
                mapFrom(m => m.poker_plo)
            ),
            forMember(
                vm => vm.sports,
                mapFrom(m => m.sports)
            ),
            forMember(
                vm => vm.total_share,
                mapFrom(m => m.total_share)
            )
        );

        createMap<PayoutReport, PayoutReportDownloadModel>(
            mapper,
            payoutReportMappingKeys.model,
            payoutReportMappingKeys.downloadModel,
            forMember(
                vm => vm.agent_uid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.agent_username,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.casino,
                mapFrom(m => m.casino?.toFixed(2))
            ),
            forMember(
                vm => vm.country,
                mapFrom(m => m.country)
            ),
            forMember(
                vm => vm.global_spins,
                mapFrom(m => m.global_spins?.toFixed(2))
            ),
            forMember(
                vm => vm.payout_report_id,
                mapFrom(m => m.payout_report_id)
            ),
            forMember(
                vm => vm.player_uid,
                mapFrom(m => m.player_uid)
            ),
            forMember(
                vm => vm.player_username,
                mapFrom(m => m.player_username)
            ),
            forMember(
                vm => vm.poker_cash,
                mapFrom(m => m.poker_cash?.toFixed(2))
            ),
            forMember(
                vm => vm.poker_mtt,
                mapFrom(m => m.poker_mtt?.toFixed(2))
            ),
            forMember(
                vm => vm.poker_plo,
                mapFrom(m => m.poker_plo?.toFixed(2))
            ),
            forMember(
                vm => vm.sports,
                mapFrom(m => m.sports?.toFixed(2))
            ),
            forMember(
                vm => vm.total_share,
                mapFrom(m => m.total_share?.toFixed(2))
            )
        );

        createMap<PayoutReportViewModelKeys, PayoutReportQueryFields>(
            mapper,
            payoutReportMappingKeys.viewModelsKeys,
            payoutReportMappingKeys.queryItemsKeys,
            constructUsing(m => {
                const queryMapper: Record<PayoutReportViewModelKeys, PayoutReportQueryFields> = {
                    agent_uid: 'agent_uid',
                    agent_username: 'agent_username',
                    casino: 'casino',
                    country: 'country',
                    global_spins: 'global_spins',
                    payout_report_id: 'payout_report_id',
                    player_uid: 'player_uid',
                    player_username: 'player_username',
                    poker_cash: 'poker_cash',
                    poker_mtt: 'poker_mtt',
                    poker_plo: 'poker_plo',
                    sports: 'sports',
                    total_share: 'total_share',
                };
                return queryMapper[m];
            })
        );
        createMap<PayoutReportViewModelKeys, PayoutReportDownloadFields>(
            mapper,
            payoutReportMappingKeys.viewModelsKeys,
            payoutReportMappingKeys.downloadFields,
            constructUsing(m => {
                const mapper: Record<PayoutReportViewModelKeys, PayoutReportDownloadFields> = {
                    agent_uid: 'agent_uid',
                    agent_username: 'agent_username',
                    casino: 'casino',
                    country: 'country',
                    global_spins: 'global_spins',
                    payout_report_id: 'payout_report_id',
                    player_uid: 'player_uid',
                    player_username: 'player_username',
                    poker_cash: 'poker_cash',
                    poker_mtt: 'poker_mtt',
                    poker_plo: 'poker_plo',
                    sports: 'sports',
                    total_share: 'total_share',
                };
                return mapper[m];
            })
        );

        createMap<PayoutReportViewModelKeys, PayoutReportColumns>(
            mapper,
            payoutReportMappingKeys.viewModelsKeys,
            payoutReportMappingKeys.columns,
            constructUsing(m => {
                const nonColumnKeys: PayoutReportViewModelKeys[] = ['payout_report_id'];
                type FieldsToMap = Exclude<PayoutReportViewModelKeys, PayoutReportColumns | (typeof nonColumnKeys)[number]>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return nonColumnKeys.includes(m) ? undefined : m;
            })
        );
        createMap<PayoutReportColumns, PayoutReportViewModelKeys>(
            mapper,
            payoutReportMappingKeys.columns,
            payoutReportMappingKeys.viewModelsKeys,
            constructUsing(m => {
                type FieldsToMap = Exclude<PayoutReportColumns, PayoutReportViewModelKeys>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return m;
            })
        );

        createMap<PayoutReportViewModelKeys, PayoutReportSortKeys>(
            mapper,
            payoutReportMappingKeys.viewModelsKeys,
            payoutReportMappingKeys.sortKeys,
            constructUsing(m => {
                const sortMapper: Record<PayoutReportViewModelKeys, PayoutReportSortKeys> = {
                    agent_uid: 'agent_uid',
                    agent_username: 'agent_username',
                    casino: 'casino',
                    country: 'country',
                    global_spins: 'global_spins',
                    payout_report_id: 'payout_report_id',
                    player_uid: 'player_uid',
                    player_username: 'player_username',
                    poker_cash: 'poker_cash',
                    poker_mtt: 'poker_mtt',
                    poker_plo: 'poker_plo',
                    sports: 'sports',
                    total_share: 'total_share',
                };
                return sortMapper[m];
            })
        );

        createMap<PayoutReportSortKeys, PayoutReportViewModelKeys>(
            mapper,
            payoutReportMappingKeys.sortKeys,
            payoutReportMappingKeys.viewModelsKeys,
            constructUsing(m => {
                const sortMapper: Record<PayoutReportSortKeys, PayoutReportViewModelKeys> = {
                    agent_uid: 'agent_uid',
                    agent_username: 'agent_username',
                    casino: 'casino',
                    country: 'country',
                    global_spins: 'global_spins',
                    payout_report_id: 'payout_report_id',
                    player_uid: 'player_uid',
                    player_username: 'player_username',
                    poker_cash: 'poker_cash',
                    poker_mtt: 'poker_mtt',
                    poker_plo: 'poker_plo',
                    sports: 'sports',
                    total_share: 'total_share',
                };
                return sortMapper[m];
            })
        );
    }
}
